import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { showBanner } from '../../global-state/banner-slice'
import {
  campaignServices,
  CreatePromotionalReferralCodePayload,
  PromotionalReferralCodeListItem,
  PromotionalReferralRules,
} from '../../services/campaign-service'
import { CampaignFormPresenter } from './campaign-form-presenter'
import {
  CAMPAIGN_RULE_OPTIONS,
  CampaignFormValues,
  CampaignRuleId,
  campaignFormDefaultValues,
} from './campaign-form-types'

function rulesForRuleId(ruleId: string): PromotionalReferralRules {
  const option = CAMPAIGN_RULE_OPTIONS.find((o) => o.id === ruleId)
  if (!option) {
    throw new Error('Please select a valid rule')
  }
  return { ...option.rules }
}

function isoToDatetimeLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}

function inferRuleId(rules: Record<string, unknown> | null | undefined): CampaignRuleId {
  if (rules && rules.waive_dealing_charges === true) return 'waive_dealing_charges'
  return CAMPAIGN_RULE_OPTIONS[0].id
}

function listItemToFormValues(row: PromotionalReferralCodeListItem): CampaignFormValues {
  return {
    code: row.code ?? '',
    description: row.description ?? '',
    ruleId: inferRuleId(row.rules as Record<string, unknown> | null | undefined),
    is_active: row.is_active !== false,
    valid_from: isoToDatetimeLocalInput(row.valid_from),
    valid_until: isoToDatetimeLocalInput(row.valid_until),
    max_redemptions:
      row.max_redemptions !== null && row.max_redemptions !== undefined
        ? String(row.max_redemptions)
        : '',
  }
}

function buildPayload(
  values: CampaignFormValues,
  options?: { forUpdate?: boolean },
): CreatePromotionalReferralCodePayload {
  const code = values.code.trim()
  if (!code) {
    throw new Error('Code is required')
  }
  if (code.length < 3) {
    throw new Error('Code must be at least 3 characters')
  }
  const rules = rulesForRuleId(values.ruleId)

  const payload: CreatePromotionalReferralCodePayload = {
    code,
    rules,
    is_active: values.is_active,
  }

  const description = values.description?.trim()
  if (description) {
    payload.description = description
  } else if (options?.forUpdate) {
    payload.description = null
  }

  payload.valid_from = values.valid_from ? new Date(values.valid_from).toISOString() : null
  payload.valid_until = values.valid_until ? new Date(values.valid_until).toISOString() : null

  if (values.max_redemptions === '') {
    payload.max_redemptions = null
  } else {
    const n = Number.parseInt(values.max_redemptions, 10)
    if (Number.isInteger(n) && n >= 1) {
      payload.max_redemptions = n
    }
  }

  return payload
}

function CampaignFormContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { campaignId } = useParams<{ campaignId: string }>()
  const isEditMode = Boolean(campaignId)

  const form = useForm<CampaignFormValues>({
    defaultValues: campaignFormDefaultValues,
    mode: 'onBlur',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isEditMode || !campaignId) return

    const state = location.state as { campaign?: PromotionalReferralCodeListItem } | undefined
    const campaign = state?.campaign
    if (campaign && String(campaign.id) === String(campaignId)) {
      form.reset(listItemToFormValues(campaign))
      return
    }

    dispatch(
      showBanner({
        text: 'Open the editor from the campaigns list.',
        variant: 'error-banner',
      }),
    )
    navigate('/campaigns', { replace: true })
  }, [isEditMode, campaignId, location.state, form, dispatch, navigate])

  const onSubmit = async (data: CampaignFormValues) => {
    let payload: CreatePromotionalReferralCodePayload
    try {
      payload = buildPayload(data, { forUpdate: isEditMode })
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Invalid form data'
      if (message.includes('Code')) {
        form.setError('code', { type: 'manual', message })
      } else if (message.includes('rule')) {
        form.setError('ruleId', { type: 'manual', message })
      } else {
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
      }
      return
    }

    if (payload.valid_from && payload.valid_until) {
      const from = new Date(payload.valid_from).getTime()
      const until = new Date(payload.valid_until).getTime()
      if (until < from) {
        form.setError('valid_until', {
          type: 'manual',
          message: 'Valid until must be on or after valid from',
        })
        return
      }
    }

    setIsSubmitting(true)
    try {
      if (isEditMode && campaignId) {
        const res = await campaignServices.updatePromotionalReferralCode(campaignId, payload)
        if (res?.status === 200) {
          dispatch(showBanner({ text: 'Campaign updated successfully', variant: 'success-banner' }))
          navigate('/campaigns')
        } else {
          dispatch(
            showBanner({
              text: res?.data?.message || 'Something went wrong. Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      } else {
        const res = await campaignServices.createPromotionalReferralCode(payload)
        if (res?.status === 200 || res?.status === 201) {
          dispatch(showBanner({ text: 'Campaign created successfully', variant: 'success-banner' }))
          form.reset(campaignFormDefaultValues)
        } else {
          dispatch(
            showBanner({
              text: res?.data?.message || 'Something went wrong. Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <CampaignFormPresenter
      form={form}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      mode={isEditMode ? 'edit' : 'create'}
    />
  )
}

export { CampaignFormContainer }
